import { useState, useCallback, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
import { Bot, Loader2 } from 'lucide-react';
import { useConversas, useToggleIA, useMetricas, useGlobalIAControl, useSetGlobalIAControl } from '@/hooks/useIAWhatsApp';
import { MetricasHeader } from '@/components/ia-whatsapp/MetricasHeader';
import { BuscaFiltros } from '@/components/ia-whatsapp/BuscaFiltros';
import { ConversaList } from '@/components/ia-whatsapp/ConversaList';
import { ConversaDetalhes } from '@/components/ia-whatsapp/ConversaDetalhes';
import { BotaoSeguranca } from '@/components/ia-whatsapp/BotaoSeguranca';
import { ConfirmacaoModal } from '@/components/ia-whatsapp/ConfirmacaoModal';
import type { Conversa } from '@/types';

function useFilteredConversas(conversas: Conversa[] | undefined, statusFilter: string) {
  return useMemo(() => {
    if (!conversas) return [];
    if (statusFilter === 'all') return conversas;

    return conversas.filter((c) => {
      switch (statusFilter) {
        case 'ativa':
          return !c.is_ai_disabled && c.msg_count_24h > 0;
        case 'desligada':
          return c.is_ai_disabled;
        case 'aguardando':
          return !c.is_ai_disabled && c.msg_count_24h === 0;
        case 'reuniao':
          return c.teve_reativacao_recente;
        case 'inativo':
          return !c.is_ai_disabled && c.msg_count_24h === 0 && !c.teve_reativacao_recente;
        default:
          return true;
      }
    });
  }, [conversas, statusFilter]);
}

function AssistenteIA() {
  // const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<{ channelId: string; currentState: boolean } | null>(null);
  const [showGlobalActionToast, setShowGlobalActionToast] = useState<string | null>(null);

  const { data, isLoading, error } = useConversas({ search, status: statusFilter !== 'all' ? statusFilter : undefined, page: 1, limit: 50 });
  const { data: metricas, isLoading: loadingMetricas } = useMetricas();
  const { data: globalControl } = useGlobalIAControl();
  const toggleIA = useToggleIA();
  const setGlobalIA = useSetGlobalIAControl();

  const conversas = data?.conversas || [];
  const filteredConversas = useFilteredConversas(conversas, statusFilter);

  const selectedConversa = useMemo(
    () => conversas.find((c) => c.channel_id === selectedChannelId) || null,
    [conversas, selectedChannelId]
  );

  const isGlobalDisabled = globalControl?.is_disabled ?? false;

  const conversasAtivasCount = useMemo(() => {
    if (isGlobalDisabled) return 0;
    return conversas.filter((c: Conversa) => !c.is_ai_disabled).length;
  }, [conversas, isGlobalDisabled]);

  const modoManualAtivo = isGlobalDisabled || (conversasAtivasCount === 0 && conversas.length > 0);

  const handleSelect = useCallback((channelId: string) => {
    setSelectedChannelId((prev) => (prev === channelId ? null : channelId));
  }, []);

  const handleToggleIA = useCallback((channelId: string, currentState: boolean) => {
    setConfirmToggle({ channelId, currentState });
  }, []);

  const executeToggle = useCallback(() => {
    if (!confirmToggle) return;
    toggleIA.mutate(
      { channelId: confirmToggle.channelId, is_ai_disabled: !confirmToggle.currentState },
      {
        onSuccess: () => {
          setConfirmToggle(null);
        },
      }
    );
  }, [confirmToggle, toggleIA]);

  const handlePausarTudo = useCallback(() => {
    setGlobalIA.mutate(
      { is_disabled: true },
      {
        onSuccess: () => {
          setShowGlobalActionToast('🛑 IA GLOBAL pausada. O n8n não responderá mais nenhuma mensagem.');
          setTimeout(() => setShowGlobalActionToast(null), 6000);
        },
      }
    );
  }, [setGlobalIA]);

  const handleLigarTudo = useCallback(() => {
    setGlobalIA.mutate(
      { is_disabled: false },
      {
        onSuccess: () => {
          setShowGlobalActionToast('▶️ IA GLOBAL ativada. O n8n voltará a responder automaticamente.');
          setTimeout(() => setShowGlobalActionToast(null), 6000);
        },
      }
    );
  }, [setGlobalIA]);

  const displayConversa = selectedConversa || (filteredConversas.length > 0 ? filteredConversas[0] : null);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Bot className="h-6 w-6 text-ds-success" />
          <h1 className="text-2xl font-bold text-ds-text-primary">Assistente IA</h1>
        </div>
        <p className="text-ds-text-secondary text-sm">
          Gerencie conversas do WhatsApp e controle quando a IA atua ou não
        </p>
      </div>

      {/* Métricas */}
      <div className="mb-6">
        <MetricasHeader metricas={metricas} isLoading={loadingMetricas} />
      </div>

      {/* Toast global */}
      {showGlobalActionToast && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-ds-success/10 border border-ds-success/20 text-ds-success text-sm flex items-center justify-between">
          <span>{showGlobalActionToast}</span>
          <button onClick={() => setShowGlobalActionToast(null)} className="text-ds-success hover:opacity-70">
            ✕
          </button>
        </div>
      )}

      {/* Status da IA - Indicador visual claro para o usuário */}
      <div className={`
        mb-4 px-4 py-3 rounded-lg border text-sm flex items-center gap-3
        ${isGlobalDisabled 
          ? 'bg-ds-danger/10 border-ds-danger/20 text-ds-danger' 
          : 'bg-ds-success/10 border-ds-success/20 text-ds-success'
        }
      `}>
        {/* Ícone animado de status */}
        <div className="relative flex-shrink-0">
          <div className={`
            w-3 h-3 rounded-full
            ${isGlobalDisabled ? 'bg-ds-danger' : 'bg-ds-success'}
          `} />
          {!isGlobalDisabled && (
            <div className="absolute inset-0 w-3 h-3 rounded-full bg-ds-success animate-ping opacity-75" />
          )}
        </div>
        
        <div className="flex-1">
          <span className="font-semibold">
            {isGlobalDisabled 
              ? 'Assistente Virtual está PAUSADO' 
              : 'Assistente Virtual está ATIVO'
            }
          </span>
          <span className="block text-xs opacity-80">
            {isGlobalDisabled 
              ? 'As mensagens não serão respondidas automaticamente. Você está no controle do atendimento.' 
              : 'Respondendo automaticamente às mensagens recebidas.'
            }
          </span>
        </div>
        
        {/* Badge de status */}
        <span className={`
          px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0
          ${isGlobalDisabled 
            ? 'bg-ds-danger/20 text-ds-danger' 
            : 'bg-ds-success/20 text-ds-success'
          }
        `}>
          {isGlobalDisabled ? 'PAUSADO' : 'ATIVO'}
        </span>
      </div>

      {/* Busca e Filtros */}
      <div className="mb-4">
        <BuscaFiltros
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </div>

      {/* Conteúdo principal */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-ds-success animate-spin" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-ds-danger text-sm">Erro ao carregar conversas</p>
            <p className="text-ds-text-secondary text-xs mt-1">Verifique sua conexão e tente novamente</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex gap-6 min-h-0">
          {/* Lista de conversas */}
          <div className="flex-1 lg:flex-[0.55] overflow-y-auto pr-2">
            <ConversaList
              conversas={filteredConversas}
              selectedChannelId={selectedChannelId}
              onSelect={handleSelect}
              onToggleIA={handleToggleIA}
            />
          </div>

          {/* Painel de detalhes */}
          <div className="hidden lg:block lg:flex-[0.45] overflow-y-auto">
            {displayConversa ? (
              <ConversaDetalhes
                conversa={displayConversa}
                onToggleIA={() => handleToggleIA(displayConversa.channel_id, displayConversa.is_ai_disabled)}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-ds-text-secondary text-sm">Selecione uma conversa para ver detalhes</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Botão de segurança FAB */}
      <BotaoSeguranca
        conversasAtivas={conversasAtivasCount}
        onPausarTudo={handlePausarTudo}
        onLigarTudo={handleLigarTudo}
        modoManualAtivo={modoManualAtivo}
      />

      {/* Modal de confirmação individual */}
      <ConfirmacaoModal
        isOpen={!!confirmToggle}
        title={confirmToggle?.currentState ? 'Ligar IA nesta conversa?' : 'Desligar IA nesta conversa?'}
        message={
          confirmToggle?.currentState
            ? 'A IA voltará a responder automaticamente nesta conversa.'
            : 'A IA será pausada nesta conversa. Você assume o controle do atendimento.'
        }
        confirmText={confirmToggle?.currentState ? 'Ligar IA' : 'Desligar IA'}
        variant={confirmToggle?.currentState ? 'primary' : 'danger'}
        onConfirm={executeToggle}
        onCancel={() => setConfirmToggle(null)}
      />
    </div>
  );
}

export { AssistenteIA };
